import React from 'react';

const InstallerDetail = ({ installer, onBack }) => {
  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercent = (value) => {
    if (!value) return 'N/A';
    return `${value}%`;
  };

  const getQualityScoreColor = (score) => {
    if (score >= 90) return '#00E676';
    if (score >= 80) return '#FFD600';
    if (score >= 70) return '#FF9100';
    return '#FF6B6B';
  };

  const styles = {
    container: {
      backgroundColor: 'rgba(13, 27, 49, 0.85)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '20px',
      padding: '2rem',
      backdropFilter: 'blur(20px)'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '2rem',
      paddingBottom: '1.5rem',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
    },
    backButton: {
      backgroundColor: 'transparent',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      color: '#B0C4DE',
      padding: '0.5rem 1rem',
      fontSize: '0.9rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    companyHeader: {
      flex: 1,
      marginRight: '2rem'
    },
    companyName: {
      fontSize: '2.5rem',
      fontWeight: '700',
      color: '#FFFFFF',
      marginBottom: '0.5rem'
    },
    companyMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      fontSize: '0.9rem',
      color: '#B0C4DE',
      marginBottom: '1rem'
    },
    lastUpdated: {
      fontSize: '0.8rem',
      color: '#7F8C9A'
    },
    qualityScore: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '12px',
      padding: '1rem',
      minWidth: '120px'
    },
    scoreNumber: {
      fontSize: '2rem',
      fontWeight: '800',
      marginBottom: '0.25rem'
    },
    scoreLabel: {
      fontSize: '0.8rem',
      color: '#B0C4DE',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    contentGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
      gap: '2rem',
      marginBottom: '2rem'
    },
    section: {
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
      border: '1px solid rgba(255, 255, 255, 0.05)',
      borderRadius: '12px',
      padding: '1.5rem'
    },
    sectionTitle: {
      fontSize: '1.2rem',
      fontWeight: '700',
      color: '#FFFFFF',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    sectionIcon: {
      width: '20px',
      height: '20px',
      color: '#2979FF'
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '1rem'
    },
    infoItem: {
      marginBottom: '0.75rem'
    },
    infoLabel: {
      fontSize: '0.8rem',
      color: '#7F8C9A',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '0.25rem'
    },
    infoValue: {
      fontSize: '0.95rem',
      color: '#FFFFFF',
      fontWeight: '500'
    },
    tagList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '0.5rem'
    },
    tag: {
      backgroundColor: 'rgba(41, 121, 255, 0.2)',
      color: '#2979FF',
      padding: '0.25rem 0.75rem',
      borderRadius: '12px',
      fontSize: '0.8rem',
      fontWeight: '500',
      border: '1px solid rgba(41, 121, 255, 0.3)'
    },
    link: {
      color: '#2979FF',
      textDecoration: 'none',
      transition: 'color 0.3s ease'
    },
    fullWidth: {
      gridColumn: '1 / -1'
    },
    performanceBar: {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '6px',
      height: '8px',
      marginTop: '0.5rem',
      overflow: 'hidden'
    },
    performanceFill: {
      height: '100%',
      borderRadius: '6px',
      transition: 'width 0.3s ease'
    },
    statsRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: '1rem',
      marginTop: '1rem'
    },
    statBox: {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      padding: '1rem',
      textAlign: 'center'
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#FFFFFF',
      marginBottom: '0.25rem'
    },
    statLabel: {
      fontSize: '0.8rem',
      color: '#B0C4DE'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.companyHeader}>
          <h1 style={styles.companyName}>{installer.company_name}</h1>
          <div style={styles.companyMeta}>
            <span>{installer.license_type}</span>
            <span>•</span>
            <span>{installer.license_status}</span>
            <span>•</span>
            <span>Expires: {formatDate(installer.license_expires)}</span>
          </div>
          <div style={styles.lastUpdated}>
            Last Updated: {formatDate(installer.updated_at)}
          </div>
        </div>
        <div style={styles.qualityScore}>
          <div 
            style={{
              ...styles.scoreNumber,
              color: getQualityScoreColor(installer.data_quality_score)
            }}
          >
            {installer.data_quality_score || 0}
          </div>
          <div style={styles.scoreLabel}>Quality Score</div>
        </div>
        <button
          style={styles.backButton}
          onClick={onBack}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          ← Back to Results
        </button>
      </div>

      {/* Content Grid */}
      <div style={styles.contentGrid}>
        {/* Primary Contact Information */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            <svg style={styles.sectionIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
            </svg>
            Primary Contact Information
          </h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Phone</div>
              <div style={styles.infoValue}>{installer.primary_phone || 'N/A'}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Secondary Phone</div>
              <div style={styles.infoValue}>{installer.secondary_phone || 'N/A'}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Email</div>
              <div style={styles.infoValue}>
                {installer.email ? (
                  <a href={`mailto:${installer.email}`} style={styles.link}>
                    {installer.email}
                  </a>
                ) : 'N/A'}
              </div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Website</div>
              <div style={styles.infoValue}>
                {installer.website ? (
                  <a href={installer.website} target="_blank" rel="noopener noreferrer" style={styles.link}>
                    {installer.website}
                  </a>
                ) : 'N/A'}
              </div>
            </div>
          </div>
        </div>

        {/* Company Profile */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            <svg style={styles.sectionIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
            </svg>
            Company Profile
          </h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Address</div>
              <div style={styles.infoValue}>
                {installer.street_address}<br/>
                {installer.city}, {installer.state} {installer.zip_code}
              </div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>County</div>
              <div style={styles.infoValue}>{installer.county}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Year Founded</div>
              <div style={styles.infoValue}>{installer.year_founded || 'N/A'}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Employees</div>
              <div style={styles.infoValue}>{formatNumber(installer.employee_count)}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Annual Revenue</div>
              <div style={styles.infoValue}>{formatCurrency(installer.annual_revenue)}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>License Number</div>
              <div style={styles.infoValue}>{installer.license_number}</div>
            </div>
          </div>
        </div>

        {/* Services & Specialties */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            <svg style={styles.sectionIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
            </svg>
            Services & Specializations
          </h3>
          <div style={styles.infoItem}>
            <div style={styles.infoLabel}>Services Offered</div>
            <div style={styles.tagList}>
              {installer.services_offered?.map((service, index) => (
                <span key={index} style={styles.tag}>{service}</span>
              )) || <span style={styles.infoValue}>N/A</span>}
            </div>
          </div>
          <div style={styles.infoItem}>
            <div style={styles.infoLabel}>Specializations</div>
            <div style={styles.tagList}>
              {installer.specializations?.map((spec, index) => (
                <span key={index} style={styles.tag}>{spec}</span>
              )) || <span style={styles.infoValue}>N/A</span>}
            </div>
          </div>
        </div>

        {/* Equipment & Brands */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            <svg style={styles.sectionIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
            </svg>
            Equipment & Brands
          </h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Panel Brands</div>
              <div style={styles.tagList}>
                {installer.panel_brands?.map((brand, index) => (
                  <span key={index} style={styles.tag}>{brand}</span>
                )) || <span style={styles.infoValue}>N/A</span>}
              </div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Inverter Brands</div>
              <div style={styles.tagList}>
                {installer.inverter_brands?.map((brand, index) => (
                  <span key={index} style={styles.tag}>{brand}</span>
                )) || <span style={styles.infoValue}>N/A</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            <svg style={styles.sectionIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
            Performance Metrics
          </h3>
          <div style={styles.statsRow}>
            <div style={styles.statBox}>
              <div style={styles.statValue}>{formatNumber(installer.total_installations)}</div>
              <div style={styles.statLabel}>Total Installations</div>
            </div>
            <div style={styles.statBox}>
              <div style={styles.statValue}>{installer.total_mw_installed?.toFixed(1) || 0}</div>
              <div style={styles.statLabel}>MW Installed</div>
            </div>
            <div style={styles.statBox}>
              <div style={styles.statValue}>{installer.avg_project_size_kw?.toFixed(1) || 0}</div>
              <div style={styles.statLabel}>Avg Project Size (kW)</div>
            </div>
            <div style={styles.statBox}>
              <div style={styles.statValue}>${installer.avg_cost_per_watt?.toFixed(2) || 0}</div>
              <div style={styles.statLabel}>Cost per Watt</div>
            </div>
          </div>
        </div>

        {/* Installation Types */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            <svg style={styles.sectionIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"/>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"/>
            </svg>
            Installation Types
          </h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Residential</div>
              <div style={styles.infoValue}>{formatPercent(installer.residential_percentage)}</div>
              <div style={styles.performanceBar}>
                <div 
                  style={{
                    ...styles.performanceFill,
                    width: `${installer.residential_percentage || 0}%`,
                    backgroundColor: '#00E676'
                  }}
                />
              </div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Commercial</div>
              <div style={styles.infoValue}>{formatPercent(installer.commercial_percentage)}</div>
              <div style={styles.performanceBar}>
                <div 
                  style={{
                    ...styles.performanceFill,
                    width: `${installer.commercial_percentage || 0}%`,
                    backgroundColor: '#2979FF'
                  }}
                />
              </div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Utility</div>
              <div style={styles.infoValue}>{formatPercent(installer.utility_percentage)}</div>
              <div style={styles.performanceBar}>
                <div 
                  style={{
                    ...styles.performanceFill,
                    width: `${installer.utility_percentage || 0}%`,
                    backgroundColor: '#FF9100'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Speed & Reliability */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            <svg style={styles.sectionIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            Speed & Reliability
          </h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Avg PTO Turnaround</div>
              <div style={styles.infoValue}>{installer.avg_pto_turnaround_days || 0} days</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>On-time Permit Rate</div>
              <div style={styles.infoValue}>{formatPercent(installer.on_time_permit_rate)}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Last Installation</div>
              <div style={styles.infoValue}>{formatDate(installer.last_installation_date)}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Decommissioning Rate</div>
              <div style={styles.infoValue}>{formatPercent(installer.decommissioning_rate)}</div>
            </div>
          </div>
        </div>

        {/* Data Source & Verification */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            <svg style={styles.sectionIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            Data Source & Verification
          </h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Data Source</div>
              <div style={styles.infoValue}>{installer.data_source || 'Multiple Sources'}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Data Verified</div>
              <div style={styles.infoValue}>{formatDate(installer.data_verified_date)}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Activity Level</div>
              <div style={{ ...styles.infoValue, textTransform: 'capitalize' }}>
                {installer.activity_level || 'N/A'}
              </div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Growth Trend</div>
              <div style={{ ...styles.infoValue, textTransform: 'capitalize' }}>
                {installer.growth_trend || 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallerDetail; 